import { Cell } from "@components/table/types";
// import { Table } from "@components";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { getEmployeeDelegates } from "@api/getEmployeeDelegates";
import { Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { DelegatesItem } from "./delegatesitem";
import {
  EmployeeDelegateItem,
  EmployeeDelegateModel,
} from "@api/types/employeeswithdelegates";
import { Form } from "@components";
import { postEmployeeDelegate } from "@api/admin/employeedelegates";
import { alertService } from "@services";
import Hooks from "@hooks";

interface Props {
  employeeId: number;
  userEmail: string;
}

type Key = keyof EmployeeDelegateItem;

// export type EmployeeDelegate = {
//   id: number;
//   userEmail: string;
//   delegateEmail: string;
//   isActive: boolean;
//   dateAdded: Date;
//   cEmployees_fk: number;
//   startDate: Date;
//   endDate: Date;
// };

// const headers: Cell[] = [
//   { data: "Delegate" },
//   { data: "State" },
//   { data: "Edit" },
// ];

export const DelegatesList: React.FC<Props> = ({ employeeId, userEmail }) => {
  const form = Form.useManager<EmployeeDelegateItem>({
    id: 0,
    userEmail: "",
    delegateEmail: "",
    isActive: false,
  });
  let DelegateItem: EmployeeDelegateItem = {
    id: 0,
    userEmail: "",
    delegateEmail: "",
    isActive: false,
  };
  const [
    directory_delegateEmails,
    ,
    directory_delegateEmails_fetching,
  ] = Hooks.useDirectory(form.values.delegateEmail);
  const [delegates, setDelegates] = useState<EmployeeDelegateModel[]>([]);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    reloadAndReset();
  }, []);

  function getDelegates() {
    setIsLoadingTable(true);
    getEmployeeDelegates(employeeId)
      .then((response) => {
        setDelegates(response);
      })
      .catch((error) => {
        throw error;
      })
      .then(() => {
        setIsLoadingTable(false);
      });
  }

  function resetForm() {
    form.values.id = 0;
    form.values.userEmail = userEmail;
    form.values.delegateEmail = "";
    form.values.isActive = false;
  }

  function reloadAndReset() {
    getDelegates();
    resetForm();
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "delegateEmail":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          sendPayload();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function sendPayload() {
    setIsSubmitting(true);
    DelegateItem.userEmail = userEmail;
    DelegateItem.delegateEmail = form.values.delegateEmail;
    DelegateItem.isActive = form.values.isActive;
    postEmployeeDelegate(employeeId, DelegateItem)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Delegate successfully added." });
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({
            msg:
              "Please check if delegate already exists for this user and try again.",
          });
        }
      })
      .then(() => {
        setIsSubmitting(false);
        reloadAndReset();
      });
  }

  // for autocompletion
  function handleDelegateEmailInput(name: string, value: string): void {
    form.set(Form.target.VALUE, ["delegateEmail", value]);
  }

  function getDirectoryOptionsDelegateEmail(): string[] {
    return directory_delegateEmails
      ? directory_delegateEmails.map((user) => user.email)
      : [];
  }

  // first React.Fragment is the form, second is the table
  return (
    <React.Fragment>
      <React.Fragment>
        <Grid item xs={12} style={{ marginBottom: "35px" }}>
          <Box border={1} borderColor="grey.500">
            <Box
              px={2}
              py={1}
              bgcolor="#8a92a5"
              color="white"
              fontWeight="fontWeightBold"
            >
              <Typography variant="body2" color="inherit">
                Add Delegate
              </Typography>
            </Box>
            <Box p={2}>
              <Grid container spacing={2}>
                <Form.Autocomplete
                  name="delegateEmail"
                  placeholder="name.lastname@globalpay.com"
                  value={form.values.delegateEmail}
                  error={form.errors.delegateEmail}
                  onChange={handleChange}
                  onInput={handleDelegateEmailInput}
                  options={getDirectoryOptionsDelegateEmail()}
                  loading={directory_delegateEmails_fetching}
                  disabled={isSubmitting}
                  size={6}
                />
                <Form.Checkbox
                  label="Delegate is active"
                  name="isActive"
                  value={form.values.isActive}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  size={4}
                />
                <Button
                  size="small"
                  style={{
                    minHeight: "30px",
                    maxHeight: "30px",
                    marginTop: "10px",
                  }}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={20} /> : "Add"}
                </Button>
              </Grid>
            </Box>
          </Box>
        </Grid>
      </React.Fragment>
      <React.Fragment>
        <Grid item xs={12}>
          <Typography variant="subtitle2">List of Delegates</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box mb={2} border={1} borderColor="grey.500" minHeight="224px">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Delegate</TableCell>
                  <TableCell style={{ width: "70px", textAlign: "center" }}>
                    State
                  </TableCell>
                  <TableCell style={{ width: "70px", textAlign: "center" }}>
                    Edit
                  </TableCell>
                  <TableCell style={{ width: "70px", textAlign: "center" }}>
                    Delete
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {delegates.map((delegate) => (
                  <DelegatesItem
                    key={delegate.id}
                    delegate={delegate}
                    resetTable={() => getDelegates()}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        </Grid>
      </React.Fragment>
    </React.Fragment>
  );

  //   return (
  //     <div>
  //       <Table
  //         data={delegates.map((data: EmployeeDelegate) => ({
  //           cells: [
  //             {
  //               data: data.delegateEmail,
  //             },
  //             {
  //               data: data.isActive ? "Active" : "Inactive",
  //             },
  //             {
  //               data: (
  //                 <IconButton
  //                   size="small"
  //                   onClick={(row) => {
  //                     console.log("clicked: " + data.delegateEmail);
  //                   }}
  //                 >
  //                   <EditIcon fontSize="small" />
  //                 </IconButton>
  //               ),
  //             },
  //           ],
  //           data: data,
  //         }))}
  //         headers={headers}
  //         tableName={"Delegates"}
  //         loading={isLoadingTable}
  //       ></Table>
  //     </div>
  //   );
};
